/**
 * SCD Lot 4 — Magasin visuel (zones & niveaux), réseau multi-magasins
 * (zones de chalandise & concurrents) et e-commerce. Helpers purs consommés
 * par retailSlice (tick), Magasin.jsx et Ecommerce.jsx.
 */
import ameliorationsData from '../data/dgs/ameliorations_magasin.json';
import zonesData from '../data/dgs/zones_chalandise.json';
import pdvData from '../data/dgs/points_de_vente.json';
import ecommerceData from '../data/dgs/ecommerce.json';
import { efficaciteEmploye } from './retail';
import { computeAmelFx, fermeturePartielle, CHANTIER_TRAFIC_MULT } from './ameliorations';
import { typesVille, bonusDominationPct, villeDuMagasin } from './villes';
import { malusConcurrence } from './parc';

export const ZONES_MAGASIN = ameliorationsData.zones;
export const getZoneMagasin = (id) => ZONES_MAGASIN.find(z => z.id === id) || null;
export const ZONES_CHALANDISE = zonesData.zones;
export const getZoneChalandise = (id) => ZONES_CHALANDISE.find(z => z.id === id) || ZONES_CHALANDISE[0];
export const TYPES_EXPANSION = pdvData.types_expansion || [];
export const getTypeExpansion = (id) => TYPES_EXPANSION.find(t => t.id === id) || null;
export const EC_PALIERS = ecommerceData.paliers;
export const EC_ACQUISITION = ecommerceData.acquisition;
export const EC_LOGISTIQUE = ecommerceData.logistique;
export const EC_RETOURS = ecommerceData.retours;
export const EC_PREPARATION = ecommerceData.preparation;
export const getPalier = (niveau) => EC_PALIERS.find(p => p.niveau === niveau) || null;

/** Niveaux par défaut : zones de base au niveau 1, zones optionnelles à 0. */
export const defaultMagasinZones = () => {
  const out = {};
  ZONES_MAGASIN.forEach(z => { out[z.id] = z.base ? 1 : 0; });
  return out;
};

export const defaultEcommerce = () => ({
  palier: 0,
  acquisition: { seo: false, pub: false, marketplaces: false },
  logistique: 'standard',
  seoDepuis: null,
  stats: { visites: 0, commandes: 0, commandesPerdues: 0, caWeb: 0, retoursEuros: 0, caWebCumul: 0, retoursCumul: 0, commandesCumul: 0 },
});

/** L'amélioration zoneId → niveau cible est-elle disponible ? Retourne { ok, reason, palier: niveauData }. */
export const peutAmeliorerZone = (magasinZones, zoneId) => {
  const zone = getZoneMagasin(zoneId);
  if (!zone) return { ok: false, reason: 'inconnu' };
  const actuel = magasinZones?.[zoneId] || 0;
  const cible = zone.niveaux.find(n => n.niveau === actuel + 1);
  if (!cible) return { ok: false, reason: 'max' };
  const deps = zone.dependances || {};
  for (const [depId, min] of Object.entries(deps)) {
    if ((magasinZones?.[depId] || 0) < min) {
      return { ok: false, reason: 'dependance', dep: getZoneMagasin(depId), depNiveau: min };
    }
  }
  return { ok: true, niveau: cible };
};

/** Agrège les effets des zones aménagées du magasin. */
export const computeMagasinEffects = (magasinZones = {}) => {
  const fx = {
    traficPct: 0, conversionPct: 0, panierPct: 0,
    capaciteCaisse: 0, capaciteReserve: 0,
    satisfactionJour: 0, demarqueMult: 1,
    partCcPct: 0, entretienJour: 0,
  };
  ZONES_MAGASIN.forEach(z => {
    const niv = magasinZones[z.id] || 0;
    z.niveaux.forEach(n => {
      if (n.niveau > niv) return;
      fx.entretienJour += (n.entretien_mensuel || 0) / 30;
      const e = n.effets || {};
      fx.traficPct += e.trafic_pct || 0;
      fx.conversionPct += e.conversion_pct || 0;
      fx.panierPct += e.panier_pct || 0;
      fx.capaciteCaisse += e.capacite_caisse || 0;
      fx.capaciteReserve += e.capacite_reserve || 0;
      fx.satisfactionJour += e.satisfaction_jour || 0;
      if (e.demarque_mult) fx.demarqueMult = Math.min(fx.demarqueMult, e.demarque_mult);
      if (e.part_cc_pct) fx.partCcPct = Math.max(fx.partCcPct, e.part_cc_pct);
    });
  });
  return fx;
};

/**
 * Contribution du réseau de magasins secondaires : chaque magasin ajoute
 * (flux_zone / flux_zone_principale) × trafic_mult_type × revenu × malus
 * concurrence à la demande, et ses loyers/charges aux coûts quotidiens.
 */
export const computeReseauEffects = (retail, totalDays = 0, opts = {}) => {
  const magasins = retail.magasins || [];
  const zonePrincipale = getZoneChalandise(retail.magasin?.zoneId);
  let demandeBonus = 0;
  let coutJour = 0;
  const detail = magasins.map(m => {
    let contribution;
    let zone = null;
    let type = null;
    if (m.coefTrafic != null) {
      // SCD R1 — magasin ouvert via le tunnel : coefficients localisation × type.
      contribution = m.coefTrafic * (m.coefPanier || 1) * (m.concurrenceMalus || 1) * 0.9;
    } else {
      // Legacy Lot 4 (zones de chalandise).
      type = getTypeExpansion(m.typeId);
      zone = getZoneChalandise(m.zoneId);
      contribution = (zone.flux_pietons_jour / Math.max(1, zonePrincipale.flux_pietons_jour))
        * (type?.trafic_mult || 0.5)
        * (zone.revenu_moyen_indice || 1)
        * (zone.concurrence_malus || 1);
    }
    // SCD R3 — améliorations propres au magasin + fermeture pendant gros travaux
    // + événement local de la ville (trafic ciblé) + domination locale (variété de formats).
    const amelFx = computeAmelFx(m);
    const evLocal = (m.evenementLocal && totalDays < m.evenementLocal.finJour) ? m.evenementLocal.traficMult : 1;
    const villeId = villeDuMagasin(m);
    const domMult = villeId ? 1 + bonusDominationPct(typesVille(retail, villeId).length) / 100 : 1;
    const concMult = villeId ? malusConcurrence(retail, villeId, totalDays) : 1;
    const liquidationMult = m.vente?.statut === 'liquidation' ? 1.35 : 1;
    contribution *= (1 + (amelFx.traficPct + amelFx.conversionPct + amelFx.panierPct) / 100)
      * (opts.merchMult ? opts.merchMult(m.secteurId || retail.secteur) : 1)
      * (fermeturePartielle(m, totalDays) ? CHANTIER_TRAFIC_MULT : 1)
      * evLocal
      * domMult
      * concMult
      * liquidationMult;
    demandeBonus += contribution;
    coutJour += ((m.murs ? 0 : m.loyerMensuel) + m.chargesMensuelles) / 30 + amelFx.entretienJour;
    return { magasin: m, type, zone, contribution: Math.round(contribution * 100) / 100 };
  });
  return {
    demandeMult: Math.round((1 + demandeBonus) * 100) / 100,
    coutJour,
    detail,
  };
};

/**
 * Tick e-commerce (pur) : calcule visites → commandes → CA web − retours,
 * prélève les ventes dans `stock` (objet DÉJÀ cloné par tickRetail, muté
 * ici volontairement) au prorata de la demande par famille.
 * Retourne null si pas de boutique en ligne active.
 */
export const computeEcommerceTick = (retail, { sec, totalDays, avisMult, promoPriceMult, stock, equipe, partCcPct = 0 }) => {
  const ec = retail.ecommerce;
  if (!ec || ec.palier < 2) return null;
  const palier = getPalier(ec.palier);
  if (!palier) return null;

  // Visites : base palier × acquisition × réputation en ligne (avis).
  let acquisitionMult = 1;
  let commissionPct = 0;
  EC_ACQUISITION.forEach(a => {
    if (!ec.acquisition?.[a.id]) return;
    let pct = a.visites_pct;
    if (a.montee_jours > 0 && ec.seoDepuis != null) {
      pct *= Math.min(1, (totalDays - ec.seoDepuis) / a.montee_jours);
    }
    acquisitionMult += pct / 100;
    if (a.commission_pct) commissionPct = a.commission_pct;
  });
  const emps = (equipe?.employes || []).filter(e => !e.finJour || e.finJour > totalDays);
  const eff = (mid) => emps.filter(e => e.metierId === mid).reduce((a, e) => a + efficaciteEmploye(e, totalDays), 0);
  const respo = Math.min(1, eff('responsable_ecommerce'));
  const noise = 0.9 + Math.random() * 0.2;
  const visites = Math.round(palier.visites_base * acquisitionMult * avisMult * (1 + respo * 0.10) * noise);

  // Conversion → commandes, plafonnées par la capacité de préparation.
  const conversion = palier.conversion_base * (1 + respo * 0.20);
  const demandees = Math.round(visites * conversion);
  const capacite = EC_PREPARATION.capacite_base_jour + eff('preparateur_commandes') * EC_PREPARATION.capacite_par_preparateur;
  let commandes = Math.min(demandees, capacite);
  const perdues = demandees - commandes;

  // Panier web = 1 achat moyen (articles_par_achat × prix moyen pondéré).
  const actives = sec.familles.filter(f => retail.assortiment?.[f.id]?.actif);
  if (actives.length === 0 || commandes <= 0) {
    return { visites, commandes: 0, perdues, caWeb: 0, margeWeb: 0, retoursEuros: 0, coutJour: (palier.cout_mensuel || 0) / 30, tvaParTaux: {}, satisfactionJour: 0, partCc: 0, capacite };
  }
  const totalPart = actives.reduce((a, f) => a + f.part_demande, 0);
  const articlesParCommande = sec.articles_par_achat || 1;

  // Prélèvement du stock au prorata + CA/marge/TVA par famille.
  let caWeb = 0;
  let margeWeb = 0;
  let unitesServies = 0;
  const tvaParTaux = {};
  const unitesVoulues = commandes * articlesParCommande;
  actives.forEach(f => {
    const st = stock[f.id] || { qty: 0, cump: f.prix_achat };
    const voulu = Math.round(unitesVoulues * (f.part_demande / totalPart));
    const servi = Math.min(voulu, st.qty);
    if (servi <= 0) return;
    const prix = Math.round((retail.assortiment[f.id].prixVente * promoPriceMult) * 100) / 100;
    caWeb += servi * prix;
    margeWeb += servi * (prix - st.cump);
    tvaParTaux[String(f.tva)] = (tvaParTaux[String(f.tva)] || 0) + servi * prix * (f.tva / 100);
    stock[f.id] = { ...st, qty: st.qty - servi };
    unitesServies += servi;
  });
  commandes = Math.min(commandes, Math.ceil(unitesServies / articlesParCommande));

  // Logistique + omnicanal : la part click & collect ne coûte rien à expédier.
  const logi = EC_LOGISTIQUE.find(l => l.id === ec.logistique) || EC_LOGISTIQUE[0];
  const partCc = Math.min(0.6, (partCcPct || 0) / 100);
  const commandesExpediees = Math.round(commandes * (1 - partCc));
  const coutExpedition = commandesExpediees * logi.cout_par_commande;

  // Retours : % du CA expédié remboursé + coût de traitement (le C&C réduit les retours).
  const tauxRetour = Math.max(0, (logi.taux_retour_pct - (partCc > 0 ? 2 : 0))) / 100;
  const retoursEuros = Math.round(caWeb * (1 - partCc) * tauxRetour);
  const coutRetours = Math.round(commandesExpediees * tauxRetour) * EC_RETOURS.cout_traitement;

  // Coûts fixes + acquisition + commission marketplaces.
  const acquisitionCout = EC_ACQUISITION.reduce((a, l) => a + (ec.acquisition?.[l.id] ? (l.cout_mensuel || 0) / 30 : 0), 0);
  const commission = commissionPct > 0 ? caWeb * (commissionPct / 100) * 0.45 : 0; // ~45 % des ventes via marketplaces

  return {
    visites,
    commandes,
    perdues,
    capacite,
    caWeb: Math.round(caWeb - retoursEuros),
    margeWeb: Math.round(margeWeb - retoursEuros),
    retoursEuros,
    tvaParTaux,
    partCc,
    satisfactionJour: logi.satisfaction_jour || 0,
    coutJour: (palier.cout_mensuel || 0) / 30 + acquisitionCout + coutExpedition + coutRetours + commission,
  };
};
